
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useMedicalRecords } from '@/hooks/useMedicalRecords';
import { useVaccinations } from '@/hooks/useVaccinations';
import { useAppointments } from '@/hooks/useAppointments';
import { Badge } from '@/components/ui/badge';
import { Paperclip, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { useToast } from '@/hooks/use-toast';

export default function MedicalRecordList() {
  const { petId } = useParams();
  const navigate = useNavigate();
  const { records, isLoading: recordsLoading, error: recordsError, deleteRecord } = useMedicalRecords(petId as string);
  const { vaccinations, isLoading: vaccinationsLoading, deleteVaccinationRecord } = useVaccinations(petId as string);
  const { appointments, isLoading: appointmentsLoading, deleteAppointmentRecord } = useAppointments(petId as string);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<{ id: string; title: string; type: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const isLoading = recordsLoading || vaccinationsLoading || appointmentsLoading;

  const handleDeleteItem = async () => {
    if (!deletingItem) return;
    
    setIsDeleting(true);
    try {
      if (deletingItem.type === 'medical_record') {
        await deleteRecord(deletingItem.id);
      } else if (deletingItem.type === 'vaccination') {
        await deleteVaccinationRecord(deletingItem.id);
      } else if (deletingItem.type === 'appointment') {
        await deleteAppointmentRecord(deletingItem.id);
      }
      toast({
        title: "Record deleted",
        description: `${deletingItem.type === 'medical_record' ? 'Medical record' : deletingItem.type} has been successfully deleted.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete record. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeletingItem(null);
    }
  };

  if (isLoading) {
    return <div className="p-4 text-center">Loading medical records...</div>;
  }

  if (recordsError) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500">Error: {recordsError.message}</p>
        <Button onClick={() => navigate(`/medical/${petId}`)} className="mt-4">
          Back to Medical Records
        </Button>
      </div>
    );
  }

  // Get display text for record type
  const getRecordTypeDisplay = (type: string) => {
    const typeMap: Record<string, string> = {
      vaccine_record: 'Vaccine Record',
      diagnosis: 'Diagnosis',
      treatment: 'Treatment',
      surgery: 'Surgery',
      medication_log: 'Medication',
      lab_result: 'Lab Result',
      general_note: 'Note',
      vaccination: 'Vaccination', // For vaccines from vaccinations table
      appointment: 'Appointment' // For appointments from appointments table
    };
    
    return typeMap[type] || 'Record';
  };

  // Get badge variant for record type
  const getRecordTypeBadge = (type: string) => {
    const variants: Record<string, { className: string }> = {
      vaccine_record: { className: 'bg-blue-50 text-blue-600 border-blue-200' },
      diagnosis: { className: 'bg-amber-50 text-amber-600 border-amber-200' },
      treatment: { className: 'bg-green-50 text-green-600 border-green-200' },
      surgery: { className: 'bg-purple-50 text-purple-600 border-purple-200' },
      medication_log: { className: 'bg-pink-50 text-pink-600 border-pink-200' },
      lab_result: { className: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
      general_note: { className: 'bg-gray-50 text-gray-600 border-gray-200' },
      vaccination: { className: 'bg-blue-50 text-blue-600 border-blue-200' },
      appointment: { className: 'bg-purple-50 text-purple-600 border-purple-200' }
    };
    
    return variants[type] || { className: 'bg-gray-50 text-gray-600 border-gray-200' };
  };

  // Combine records, vaccinations, and appointments into a unified list
  const combinedRecords = [
    // Add custom records
    ...(records || []).map(record => ({
      id: record.id,
      title: record.title,
      type: record.record_type,
      date: new Date(record.record_date),
      description: record.description || '',
      recordType: 'medical_record',
      hasAttachments: record.has_attachments || false
    })),
    // Add vaccinations as records
    ...(vaccinations || []).map(vaccination => ({
      id: vaccination.id,
      title: vaccination.vaccine_name,
      type: 'vaccination',
      date: vaccination.date_given ? new Date(vaccination.date_given) : new Date(vaccination.date_due),
      description: vaccination.notes || `Due: ${new Date(vaccination.date_due).toLocaleDateString()}`,
      recordType: 'vaccination',
      hasAttachments: false
    })),
    // Add appointments as records
    ...(appointments || []).map(appointment => ({
      id: appointment.id,
      title: appointment.appointment_type || 'Appointment',
      type: 'appointment',
      date: new Date(appointment.appointment_datetime),
      description: appointment.notes || `With: ${appointment.vet_name || 'Veterinarian'}`,
      recordType: 'appointment',
      hasAttachments: false
    }))
  ];

  // Sort combined records by date (newest first)
  const sortedRecords = combinedRecords.sort((a, b) => b.date.getTime() - a.date.getTime());

  return (
    <div className="p-4">
      {sortedRecords.length > 0 ? (
        <div className="space-y-4">
          {sortedRecords.map((record) => (
            <Card key={`${record.recordType}-${record.id}`} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold">{record.title}</h3>
                    <p className="text-xs text-gray-500">
                      {format(record.date, 'PPP')}
                    </p>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={getRecordTypeBadge(record.type).className}
                  >
                    {getRecordTypeDisplay(record.type)}
                  </Badge>
                </div>
                
                <p className="text-sm text-gray-700 mt-2">{record.description}</p>
                
                {record.hasAttachments && (
                  <div className="mt-3 flex items-center text-xs text-gray-500">
                    <Paperclip className="h-3 w-3 mr-1" />
                    <span>Has attachments</span>
                  </div>
                )}
                
                <div className="flex gap-2 mt-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="p-0 h-auto text-pet-purple text-sm"
                    onClick={() => {
                      if (record.recordType === 'medical_record') {
                        navigate(`/medical/${petId}/records/${record.id}`);
                      } else if (record.recordType === 'vaccination') {
                        navigate(`/medical/${petId}/vaccinations/edit/${record.id}`);
                      } else if (record.recordType === 'appointment') {
                        navigate(`/medical/${petId}/appointments/edit/${record.id}`);
                      }
                    }}
                  >
                    {record.recordType === 'medical_record' ? 'View Details' : 'Edit'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-0 h-auto text-red-600 text-sm hover:text-red-800"
                    onClick={() => {
                      setDeletingItem({ id: record.id, title: record.title, type: record.recordType });
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 p-8 rounded-lg border border-dashed border-gray-300 text-center">
          <p className="text-gray-500">No medical records yet</p>
          <p className="text-gray-500 text-sm mt-1">
            Add health records to keep track of your pet's medical history
          </p>
        </div>
      )}

      <Button
        className="w-full mt-6 bg-pet-purple hover:bg-pet-purple/90"
        onClick={() => navigate(`/medical/${petId}/records/new`)}
      >
        Add Medical Record
      </Button>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteItem}
        title="Delete Record"
        description="Are you sure you want to delete this record?"
        itemName={deletingItem?.title || ''}
        isLoading={isDeleting}
      />
    </div>
  );
}
